import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './NewProduct.css'; // Usamos tus mismos estilos
import baseURL from '../../url';
import { faUpload, faFileExcel, faImages } from '@fortawesome/free-solid-svg-icons';

export default function CargaMasiva() {
    const [modalOpen, setModalOpen] = useState(false);
    const [excelData, setExcelData] = useState([]);
    const [imagenes, setImagenes] = useState([]);
    const [resultadoValidacion, setResultadoValidacion] = useState(null);
    
    // Estado para detectar cuando se arrastra una imagen por encima
    const [isDraggingImg, setIsDraggingImg] = useState(false);

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        setExcelData([]);
        setImagenes([]);
        setResultadoValidacion(null);
        setIsDraggingImg(false);
    };

    // 1. Leer el archivo Excel
    const handleExcelUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            // MODIFICACIÓN: Agregamos { range: 10 } para saltar las primeras 10 filas
            let jsonData = XLSX.utils.sheet_to_json(worksheet, { range: 10 });

            // FILTRO LIMPIADOR: Si dice "Doble clic", lo pasamos a vacío para que no busque esa imagen
            jsonData = jsonData.map(prod => {
                if (prod.imagen1 === 'Doble clic') prod.imagen1 = '';
                if (prod.imagen2 === 'Doble clic') prod.imagen2 = '';
                if (prod.imagen3 === 'Doble clic') prod.imagen3 = '';
                if (prod.imagen4 === 'Doble clic') prod.imagen4 = '';
                return prod;
            });

            setExcelData(jsonData);
        };
        reader.readAsArrayBuffer(file);
        
        // Limpiamos el input
        e.target.value = null;
    };

    // 2. Lógica central para guardar las imágenes (usada por clic y por arrastre)
    const procesarImagenes = (nuevosArchivos) => {
        setImagenes(prevImagenes => {
            // Unimos las imágenes que ya estaban con las nuevas
            const todosLosArchivos = [...prevImagenes, ...nuevosArchivos];
            
            // Filtramos para eliminar duplicados basados en el nombre del archivo
            const archivosUnicos = Array.from(
                new Map(todosLosArchivos.map(file => [file.name, file])).values()
            );
            
            return archivosUnicos;
        });

        // Ocultamos el resultado anterior para forzar a validar de nuevo
        setResultadoValidacion(null);
    };

    // Acción de Clic para subir imágenes
    const handleImagesUpload = (e) => {
        procesarImagenes(Array.from(e.target.files));
        e.target.value = null; // Limpiamos el valor del input
    };

    // Acciones de Arrastrar y Soltar para imágenes
    const handleDragOverImg = (e) => {
        e.preventDefault();
        setIsDraggingImg(true); // Activa el cambio de color CSS
    };

    const handleDropImg = (e) => {
        e.preventDefault();
        setIsDraggingImg(false); // Desactiva el color CSS
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            procesarImagenes(Array.from(e.dataTransfer.files));
        }
    };

    // 3. Motor de validación (Cruzar Excel vs Imágenes cargadas)
    const validarDatos = () => {
        if (excelData.length === 0) {
            Swal.fire('Error', 'Sube un archivo Excel con productos', 'warning');
            return;
        }

        const nombresImagenesCargadas = imagenes.map(img => img.name);
        let imagenesFaltantes = [];
        let productosListos = 0;

        excelData.forEach((producto, index) => {
            // MODIFICACIÓN: Cambiamos +2 por +12 porque los productos reales empiezan en la fila 12 del Excel
            const rowNum = index + 12; 
            const imgsDelProducto = [producto.imagen1, producto.imagen2, producto.imagen3, producto.imagen4].filter(Boolean);
            
            let leFaltan = false;
            imgsDelProducto.forEach(imgName => {
                if (!nombresImagenesCargadas.includes(imgName)) {
                    imagenesFaltantes.push(`Fila ${rowNum} (${producto.titulo || 'Sin titulo'}): Falta ${imgName}`);
                    leFaltan = true;
                }
            });

            if (!leFaltan) productosListos++;
        });

        setResultadoValidacion({
            totalProductos: excelData.length,
            productosListos,
            imagenesFaltantes
        });
    };

    const enviarAlServidor = async () => {
        Swal.fire({
            title: 'Subiendo datos...',
            text: 'Esto puede tardar unos segundos, no cierres la ventana.',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        const formData = new FormData();
        
        // 1. Agregamos el array del Excel convertido a texto (String JSON)
        formData.append('productos', JSON.stringify(excelData));

        // 2. Agregamos todas las imágenes al FormData
        imagenes.forEach((file) => {
            formData.append('imagenes[]', file);
        });

        try {
            const response = await fetch(`${baseURL}/productosMasivoPost.php`, {
                method: 'POST',
                body: formData,
                credentials: 'include' // Para mantener la sesión de usuario si la ocupas
            });

            const data = await response.json();

            if (data.error) {
                Swal.fire('Error', data.error, 'error');
            } else {
                // Alerta de éxito con el resumen
                Swal.fire({
                    icon: 'success',
                    title: '¡Carga Masiva Exitosa!',
                    html: `
                        <p>Productos guardados: <b>${data.productosInsertados} / ${data.totalEnviados}</b></p>
                        <p>Imágenes guardadas: <b>${data.imagenesSubidas}</b></p>
                    `,
                    confirmButtonText: 'Genial'
                }).then(() => {
                    window.location.reload(); // Recarga para ver los productos nuevos en la tabla
                });
            }
        } catch (error) {
            console.error('Error en carga masiva:', error);
            Swal.fire('Error de conexión', 'No se pudo llegar al servidor.', 'error');
        }
    };

    return (
        <>
            <button onClick={toggleModal} className='btnSave' style={{ backgroundColor: '#28a745', marginLeft: '10px' }}>
                <FontAwesomeIcon icon={faUpload} /> Carga Masiva
            </button>

            {modalOpen && (
                <div className="modal">
                    <div className="modal-content">
                        <div className='deFlexBtnsModal'>
                            <button className='selected'>Carga Masiva de Productos</button>
                            <span className="close" onClick={toggleModal}>&times;</span>
                        </div>
                        
                        <div style={{ padding: '20px' }}>
                            <div className="upload-container">
                                {/* Zona de Excel (Sólo clic) */}
                                <div className="upload-box">
                                    <FontAwesomeIcon icon={faFileExcel} className="upload-icon excel-icon" />
                                    <div className="upload-text">
                                        <h4>1. Plantilla de datos (.xlsm)</h4>
                                        <p style={{ color: excelData.length > 0 ? '#28a745' : '#aaa' }}>
                                            {excelData.length > 0 ? `✔ ${excelData.length} productos listos en memoria.` : "Sube el archivo Excel con los productos."}
                                        </p>
                                    </div>
                                    <input type="file" id="excel-upload" accept=".xlsx, .xls, .xlsm" onChange={handleExcelUpload} hidden />
                                    <label htmlFor="excel-upload" className="btn-upload">Examinar</label>
                                </div>

                                {/* Zona de Imágenes (Clic + Drag&Drop) */}
                                <div 
                                    className={`upload-box ${isDraggingImg ? 'drag-active' : ''}`}
                                    onDragOver={handleDragOverImg}
                                    onDragLeave={() => setIsDraggingImg(false)}
                                    onDrop={handleDropImg}
                                >
                                    <FontAwesomeIcon icon={faImages} className="upload-icon img-icon" />
                                    <div className="upload-text">
                                        <h4>2. Archivos de imagen (.jpg, .png)</h4>
                                        <p style={{ color: imagenes.length > 0 ? '#28a745' : '#aaa' }}>
                                            {imagenes.length > 0 ? `✔ ${imagenes.length} imágenes listas en memoria.` : "Arrastra las imágenes aquí o examina."}
                                        </p>
                                    </div>
                                    <input type="file" id="img-upload" multiple accept="image/*" onChange={handleImagesUpload} hidden />
                                    <label htmlFor="img-upload" className="btn-upload">Examinar</label>
                                </div>
                            </div>

                            <button type="button" onClick={validarDatos} className='btnPost' style={{ width: '100%', padding: '12px', fontSize: '16px', marginBottom: '15px' }}>
                                Validar Archivos
                            </button>

                            {resultadoValidacion && (
                                <div className="resumen-box">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <h4 style={{ margin: 0 }}>Resumen de Validación</h4>
                                        <span style={{ backgroundColor: '#333', padding: '5px 10px', borderRadius: '5px', fontSize: '12px' }}>
                                            Total a subir: {resultadoValidacion.totalProductos}
                                        </span>
                                    </div>
                                    
                                    {resultadoValidacion.imagenesFaltantes.length > 0 ? (
                                        <>
                                            <p style={{ color: '#ff4d4d', margin: '10px 0 5px 0' }}><strong>⚠️ Imágenes faltantes detectadas:</strong></p>
                                            <ul className="lista-errores">
                                                {resultadoValidacion.imagenesFaltantes.map((err, i) => <li key={i}>{err}</li>)}
                                            </ul>
                                            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                                <button onClick={() => setResultadoValidacion(null)} className='btnSave' style={{ backgroundColor: '#555', flex: 1 }}>Regresar y añadir</button>
                                                <button onClick={enviarAlServidor} className='btnPost' style={{ backgroundColor: '#d33', flex: 1 }}>Forzar subida</button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ backgroundColor: 'rgba(40, 167, 69, 0.1)', padding: '15px', borderRadius: '5px', borderLeft: '3px solid #28a745', marginTop: '15px' }}>
                                                <p style={{ color: '#28a745', margin: 0 }}><strong>¡Todo coincide perfectamente!</strong> Las imágenes y el Excel están completos.</p>
                                            </div>
                                            <button onClick={enviarAlServidor} className='btnPost' style={{ width: '100%', marginTop: '15px', padding: '12px' }}>
                                                Comenzar carga al servidor
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}